import { Link } from '@tanstack/react-router'
import React from 'react'
import {
  LayoutDashboard, Users, Building2, Home, Handshake,
  CalendarDays, ClipboardCheck, Bot, ArrowUpDown, Settings,
  ChevronLeft, ChevronRight, type LucideIcon,
} from 'lucide-react'
import { useUserStore } from '@/stores/userStore'
import { useNotificationStore } from '@/stores/notificationStore'

interface NavItem { to: string; label: string; Icon: LucideIcon; adminOnly?: boolean }

const NAV_ITEMS: NavItem[] = [
  { to: '/dashboard',     label: 'Dashboard',    Icon: LayoutDashboard },
  { to: '/contacts',      label: 'Leads',        Icon: Users },
  { to: '/accounts',      label: 'Brokerages',   Icon: Building2 },
  { to: '/properties',    label: 'Properties',   Icon: Home },
  { to: '/deals',         label: 'Deals',        Icon: Handshake },
  { to: '/calendar',      label: 'Calendar',     Icon: CalendarDays },
  { to: '/approvals',     label: 'Approvals',    Icon: ClipboardCheck },
  { to: '/ai-console',    label: 'AI Swarm',     Icon: Bot },
  { to: '/import-export', label: 'Import/Export',Icon: ArrowUpDown },
  { to: '/admin',         label: 'Admin',        Icon: Settings, adminOnly: true },
]

const NAV_SECTIONS = [
  { label: 'Overview',  items: ['/dashboard'] },
  { label: 'CRM',       items: ['/contacts', '/accounts', '/properties', '/deals'] },
  { label: 'Tools',     items: ['/calendar', '/approvals', '/ai-console', '/import-export', '/admin'] },
]

/* Sidebar: black base, white labels, gold solid active */
const SIDEBAR_BG  = 'linear-gradient(180deg, #0b1120 0%, #0d1525 55%, #0b1120 100%)'
const ITEM_BASE   = { color: 'rgba(255,255,255,0.82)', borderRadius: '10px', transition: 'all 0.18s ease' }
const ITEM_HOVER  = { color: '#ffffff', background: 'rgba(166,139,91,0.18)', boxShadow: '0 0 0 1px rgba(166,139,91,0.22)' }
const ITEM_ACTIVE = { color: '#0d1525', background: 'linear-gradient(135deg,#C4A980,#A68B5B)', boxShadow: '0 2px 12px rgba(166,139,91,0.35)', fontWeight: 700 }

function NavLink({ to, label, Icon, badge }: { to: string; label: string; Icon: LucideIcon; badge?: React.ReactNode }) {
  const [hovered, setHovered] = React.useState(false)

  return (
    <Link
      to={to}
      className="flex items-center gap-3 px-3 py-2.5 text-sm font-medium w-full"
      style={ITEM_BASE}
      activeProps={{ style: ITEM_ACTIVE }}
      onMouseEnter={(e) => { setHovered(true); Object.assign((e.currentTarget as HTMLElement).style, ITEM_HOVER) }}
      onMouseLeave={(e) => {
        setHovered(false)
        const el = e.currentTarget as HTMLElement
        Object.assign(el.style, { color: ITEM_BASE.color, background: '', boxShadow: '', fontWeight: '' })
      }}
    >
      <Icon size={16} className="flex-shrink-0" />
      <span className="flex-1 truncate">{label}</span>
      {badge}
    </Link>
  )
}

function NavLinkCollapsed({ to, label, Icon }: { to: string; label: string; Icon: LucideIcon }) {
  return (
    <Link
      to={to}
      className="flex items-center justify-center py-2.5 w-full rounded-xl text-sm"
      style={ITEM_BASE}
      activeProps={{ style: ITEM_ACTIVE }}
      onMouseEnter={(e) => Object.assign((e.currentTarget as HTMLElement).style, ITEM_HOVER)}
      onMouseLeave={(e) => Object.assign((e.currentTarget as HTMLElement).style, { color: ITEM_BASE.color, background: '', boxShadow: '', fontWeight: '' })}
      title={label}
    >
      <Icon size={17} />
    </Link>
  )
}

export function Sidebar() {
  const [collapsed, setCollapsed] = React.useState(false)
  const user = useUserStore((s) => s.user)
  const { unreadCount } = useNotificationStore()
  const pendingApprovals = unreadCount()
  const visibleItems = NAV_ITEMS.filter((i) => !i.adminOnly || user?.role === 'admin')

  return (
    <aside
      className={`${collapsed ? 'w-[64px]' : 'w-[220px]'} h-full flex flex-col shrink-0 transition-all duration-300`}
      style={{ background: SIDEBAR_BG, borderRight: '1px solid rgba(166,139,91,0.20)' }}
    >
      {/* ── Brand ─────────────────────────────────────────────────────── */}
      <div className={`h-[62px] flex items-center ${collapsed ? 'justify-center px-3' : 'px-5'} flex-shrink-0`}
        style={{ borderBottom: '1px solid rgba(166,139,91,0.22)' }}>
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 font-black text-sm"
            style={{ background: 'linear-gradient(135deg, #A68B5B, #C4A980)', color: '#1E305C', boxShadow: '0 2px 10px rgba(166,139,91,0.4)' }}>
            A
          </div>
          {!collapsed && (
            <div className="min-w-0 leading-tight">
              <div className="font-black text-sm tracking-tight" style={{ color: '#F5F1EC' }}>
                Acufy <span style={{ color: '#C4A980', fontWeight: 500 }}>CRM</span>
              </div>
              <div className="text-[9px] font-semibold tracking-widest uppercase" style={{ color: 'rgba(196,169,128,0.45)' }}>
                Real Estate Intelligence
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ── Navigation ────────────────────────────────────────────────── */}
      <nav className="flex-1 px-2 py-4 overflow-y-auto" style={{ scrollbarWidth: 'none' }}>
        {collapsed ? (
          <div className="space-y-0.5">
            {visibleItems.map(({ to, label, Icon }) => (
              <NavLinkCollapsed key={to} to={to} label={label} Icon={Icon} />
            ))}
          </div>
        ) : (
          NAV_SECTIONS.map((section) => {
            const sectionItems = visibleItems.filter((i) => section.items.includes(i.to))
            if (!sectionItems.length) return null
            return (
              <div key={section.label} className="mb-5">
                <p className="px-3 mb-1 text-[9px] font-bold uppercase tracking-widest"
                  style={{ color: 'rgba(196,169,128,0.55)' }}>
                  {section.label}
                </p>
                <div className="space-y-0.5">
                  {sectionItems.map(({ to, label, Icon }) => (
                    <NavLink
                      key={to}
                      to={to}
                      label={label}
                      Icon={Icon}
                      badge={
                        to === '/approvals' && pendingApprovals > 0 ? (
                          <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1
                            text-[9px] font-bold rounded-full"
                            style={{ background: '#A68B5B', color: '#1E305C' }}>
                            {pendingApprovals > 99 ? '99+' : pendingApprovals}
                          </span>
                        ) : to === '/ai-console' ? (
                          <span className="text-[8px] font-bold px-1.5 py-0.5 rounded-full tracking-wider"
                            style={{ background: 'rgba(166,139,91,0.2)', color: '#C4A980', border: '1px solid rgba(166,139,91,0.25)' }}>
                            AI
                          </span>
                        ) : undefined
                      }
                    />
                  ))}
                </div>
              </div>
            )
          })
        )}
      </nav>

      {/* ── Footer ────────────────────────────────────────────────────── */}
      <div className="px-2 pb-3 flex-shrink-0" style={{ borderTop: '1px solid rgba(166,139,91,0.22)' }}>
        {!collapsed && user?.name && (
          <div className="flex items-center gap-2.5 px-3 py-2.5 mx-0 mt-2 mb-1 rounded-xl"
            style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(166,139,91,0.22)' }}>
            <div className="w-7 h-7 rounded-full flex-shrink-0 flex items-center justify-center text-[11px] font-black"
              style={{ background: 'linear-gradient(135deg,#A68B5B,#C4A980)', color: '#1E305C' }}>
              {user.name.split(' ').map((n) => n[0]).join('').substring(0, 2).toUpperCase()}
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-xs font-semibold truncate leading-tight" style={{ color: '#F5F1EC' }}>{user.name}</p>
              <p className="text-[10px] capitalize" style={{ color: 'rgba(196,169,128,0.55)' }}>{user.role ?? 'Agent'}</p>
            </div>
          </div>
        )}
        <button
          onClick={() => setCollapsed((c) => !c)}
          className="w-full flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium mt-1 transition-all duration-150"
          style={{ color: 'rgba(196,169,128,0.45)' }}
          onMouseEnter={(e) => { Object.assign((e.currentTarget as HTMLElement).style, { color: 'rgba(245,241,236,0.75)', background: 'rgba(255,255,255,0.06)' }) }}
          onMouseLeave={(e) => { Object.assign((e.currentTarget as HTMLElement).style, { color: 'rgba(196,169,128,0.45)', background: 'transparent' }) }}
        >
          {collapsed ? <ChevronRight size={14} /> : <><ChevronLeft size={14} /><span>Collapse</span></>}
        </button>
      </div>
    </aside>
  )
}
